import React, { useContext, useState, useEffect } from "react";
import ModuleSidebar from "../../components/ModuleSidebar/ModuleSidebar";
import { useLanguage } from "../../LanguageProvider";
import { TbFolder, TbFolders } from "react-icons/tb";
import "./Documentation.css";
import { ContentState, EditorState, convertFromHTML } from "draft-js";
import { convertToHTML } from "draft-convert";
import Dropdown from "../../components/Dropdown/Dropdown";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { TbPlus } from "react-icons/tb";
import { GeneralContext } from "../../GeneralProvider";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2pdf from 'html2pdf.js';
import ApiDocumentation from "./PDF/ApiDocumentation";

export default function Documentation() {
  const { getTranslation } = useLanguage();
  const {
    collections,
    handleGetDocumentation,
    selectedDocumentation,
    setSelectedDocumentation,
    handleUpdateDocumentation,
    selectedDocumentationID,
    setSelectedDocumentationID,
    selectedProjectID,
    getUserAuthorizations,
  } = useContext(GeneralContext);
  const [editMode, setEditMode] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("get");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [parameters, setParameters] = useState([]);
  const [editBaseURL, setEditBaseURL] = useState("");
  const [editEndpoint, setEditEndpoint] = useState("");
  const [title, setTitle] = useState("");
  const [tempParameters, setTempParameters] = useState([]);
  const { baseUrl, endpoint } = selectedDocumentation?.parameters?.url
    ? extractBaseUrlAndEndpoint(selectedDocumentation?.parameters?.url)
    : { baseUrl: "-", endpoint: "-" };
  const [projectAuthorization, setProjectAuthorization] = useState(null);
  const [selectedCollectionID, setSelectedCollectionID] = useState("");
  const [hasWritePermission, setHasWritePermission] = useState("read");

  const methodOptions = [
    { value: "get", label: "GET" },
    { value: "post", label: "POST" },
    { value: "put", label: "PUT" },
    { value: "delete", label: "DELETE" },
  ];

  const typeOptions = [
    { value: "Number (Integer)", label: "Number (Integer)" },
    { value: "Number (Float)", label: "Number (Float)" },
    { value: "String", label: "String" },
    { value: "Boolean", label: "Boolean" },
    { value: "Array (Number (Integer))", label: "Array (Number (Integer))" },
    { value: "Array (Number (Float))", label: "Array (Number (Float))" },
    { value: "Array (String)", label: "Array (String)" },
    { value: "Array (Object)", label: "Array (Object)" },
    { value: "Object", label: "Object" },
    { value: "Null", label: "Null" },
    { value: "Unknown", label: "Unknown" },
  ];

  useEffect(() => {
    if (projectAuthorization) {
      let hasWritePermissionVar =
        projectAuthorization?.authorizations === "all" ||
        projectAuthorization?.authorizations?.collections === "all" ||
        projectAuthorization?.authorizations?.collections?.some(
          (auth) =>
            auth.id === selectedCollectionID && auth.authorization !== "read"
        );
      setHasWritePermission(hasWritePermissionVar);
    }
  }, [selectedProjectID, projectAuthorization]);

  const transformCollectionsToMenus = (collections) => {
    if (!collections || collections.length === 0) {
      return [];
    }

    return collections.map((collection) => {
      // Ungroup API'lerini direkt listele
      const ungroupItems = collection.ungroup
        ? collection.ungroup.map((api) => ({
          title: api.apiTitle,
          id: api.apiId,
          method: api.method,
          url: api?.url,
          auth: api.auth,
          headers: api.headers,
          body: api.body,
          collectionId: collection.collectionId,
          isTemporary: api.isTemporary || false,
        }))
        : [];

      // Group API'lerini nested menü olarak düzenle
      const groupMenus =
        collection.groups && collection.groups.length > 0
          ? collection.groups.map((group) => ({
            title: group.title, // Grup başlığı
            id: group.groupId,
            icon: <TbFolder size={18} />,
            // Her bir grup için nestedSubmenu oluşturuyoruz
            nestedSubmenu: group.apis
              ? group.apis.map((api) => ({
                title: api.apiTitle,
                id: api.apiId,
                method: api.method,
                url: api?.url,
                auth: api.auth,
                headers: api.headers,
                body: api.body,
                collectionId: collection.collectionId,
                isTemporary: api.isTemporary || false,
              }))
              : [],
          }))
          : [];

      // Alt menüleri birleştiriyoruz: ungroupItems düz liste, groupMenus ise nested
      const submenuItems = [];
      if (ungroupItems.length > 0) submenuItems.push(...ungroupItems);
      if (groupMenus.length > 0) submenuItems.push(...groupMenus);

      return {
        sections: [
          {
            title: collection.collectionTitle,
            id: collection.collectionId,
            icon: <TbFolder size={20} />,
            submenu: submenuItems,
          },
        ],
      };
    });
  };


  const menus = transformCollectionsToMenus(collections || []);

  const handleSubmenuClick = (submenu) => {
    if (selectedDocumentation.apiId === submenu.id) {
      setSelectedDocumentationID("");
      setSelectedDocumentation({});
      setSelectedCollectionID("");
    } else {
      setSelectedDocumentationID(submenu.id);
      handleGetDocumentation(submenu.id);
      setSelectedCollectionID(submenu?.collectionId);
    }
  };

  const handleReCreate = () => {
    if (!selectedDocumentation || !selectedDocumentation.apiId) return;

    let updatedApi = null;

    for (const collection of collections) {
      // Ungroup içinden ara
      if (Array.isArray(collection.ungroup)) {
        const foundApi = collection.ungroup.find(
          api =>
            api.apiId === selectedDocumentation.apiId ||
            api.id === selectedDocumentation.apiId
        );
        if (foundApi) {
          updatedApi = foundApi;
          break;
        }
      }

      // Gruplar içinden ara
      if (Array.isArray(collection.groups)) {
        for (const group of collection.groups) {
          if (Array.isArray(group.apis)) {
            const foundApi = group.apis.find(
              api =>
                api.apiId === selectedDocumentation.apiId ||
                api.id === selectedDocumentation.apiId
            );
            if (foundApi) {
              updatedApi = foundApi;
              break;
            }
          }
        }
        if (updatedApi) break;
      }
    }

    if (!updatedApi) {
      console.error("Güncel API bilgisi collections içinde bulunamadı");
      return;
    }

    const newParameters = {
      title: updatedApi.apiTitle || updatedApi.title,
      method: updatedApi.method,
      url: updatedApi.url,
      body: updatedApi.body,
    };

    const updatedDescription = selectedDocumentation.description;
    const updateData = {
      parameters: newParameters,
      ...(updatedDescription && updatedDescription.trim()
        ? { description: updatedDescription }
        : {}),
    };

    handleUpdateDocumentation(
      selectedDocumentation.apiId,
      selectedDocumentation.id,
      updateData
    );
  };

  const handleSave = () => {
    const contentState = editorState.getCurrentContent();
    let description = convertToHTML(contentState);

    const updateData = {
      parameters: {
        body: parameters,
        method: selectedMethod,
        title: title,
        url: editBaseURL + editEndpoint,
      },
      ...(description.trim() ? { description } : {}),
    };

    handleUpdateDocumentation(
      selectedDocumentation.apiId,
      selectedDocumentation.id,
      updateData
    );

    setEditMode(false);
  };

  const handleEditClicked = (_editMode) => {
    if (_editMode) {
      handleSave();
    } else {
      setTempParameters(parameters);
      setEditMode(!editMode);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setParameters(tempParameters); // Restore original parameters
  };

  const handleAddParameter = () => {
    setParameters([
      ...parameters,
      { key: "", type: "String", description: "", required: false },
    ]);
  };

  const handleDeleteParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    const updatedParameters = [...parameters];
    updatedParameters[index] = { ...updatedParameters[index], [field]: value };
    setParameters(updatedParameters);
  };

  const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  };

  const getDescriptionContent = () => {
    const description = selectedDocumentation.description;
    if (!description || description?.trim() === "") {
      return <span>{getTranslation("noDescription")}</span>;
    }
    return <div dangerouslySetInnerHTML={{ __html: description }} />;
  };

  const checkType = (value) => {
    if (Array.isArray(value)) {
      return "Array";
    } else if (typeof value === "string") {
      return "String";
    } else {
      return "Other";
    }
  };

  function extractParamsAndTypes(body) {
    function getType(value) {
      if (typeof value === "number") {
        return Number.isInteger(value) ? "Number (Integer)" : "Number (Float)";
      }
      if (typeof value === "string") {
        return "String";
      }
      if (typeof value === "boolean") {
        return "Boolean";
      }
      if (Array.isArray(value)) {
        const itemType = value.length > 0 ? getType(value[0]) : "Unknown";
        return `Array (${itemType})`;
      }
      if (value === null) {
        return "Null";
      }
      if (typeof value === "object") {
        return "Object";
      }
      return "Unknown";
    }

    function processObject(obj, parentKey = "") {
      const result = [];

      for (const [key, value] of Object.entries(obj)) {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const type = getType(value);

        if (type === "Object") {
          result.push({
            key: fullKey,
            type: "Object",
            description: "",
            required: false,
          });
          result.push(...processObject(value, fullKey));
        } else if (type.startsWith("Array")) {
          result.push({
            key: fullKey,
            type: type,
            description: "",
            required: false,
          });
          if (value.length > 0 && typeof value[0] === "object") {
            result.push(...processObject(value[0], fullKey)); // Process the structure of the first item
          }
        } else {
          result.push({
            key: fullKey,
            type: type,
            description: "",
            required: false,
          });
        }
      }

      return result;
    }

    try {
      const parsedBody = JSON.parse(body);
      return processObject(parsedBody);
    } catch (error) {
      console.error("Invalid JSON provided:", error);
      return [];
    }
  }

  function extractBaseUrlAndEndpoint(url) {
    console.log(url)
    if (!url || typeof url !== "string") {
      return { baseUrl: "-", endpoint: "-" };
    }

    try {
      // Eğer gerçek URL ise
      const parsedUrl = new URL(url);
      return {
        baseUrl: `${parsedUrl.protocol}//${parsedUrl.host}`,
        endpoint: parsedUrl.pathname || "/",
      };
    } catch (err) {
      // 🔥 Postman environment değişkenleri gibi durumlarda buraya düşer:
      // {{easy_stage}}/users/get-user

      // Bu durumda kendimiz kaba bir ayrıştırma yapıyoruz:
      const idx = url.indexOf("/");
      if (idx === -1) {
        return { baseUrl: url, endpoint: "/" };
      }

      const base = url.substring(0, idx);
      const endpoint = url.substring(idx);

      return {
        baseUrl: base || "-",
        endpoint: endpoint || "-",
      };
    }
  }

  const handleExportPDF = (templateId) => {
    const element = document.getElementById(templateId);
    const options = {
      margin: 10,
      filename: `${templateId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(options).toPdf().output('bloburl')
      .then(url => window.open(url, '_blank'));
  };

  const fetchAuthorizations = async () => {
    const res = await getUserAuthorizations();

    if (res?.result?.length > 0) {
      const matchedProject = res?.result?.find(
        (item) => item.projectID === selectedProjectID
      );

      if (matchedProject) {
        setProjectAuthorization(matchedProject.authorizations);
      } else {
        setProjectAuthorization(null);
      }
    }
  };

  useEffect(() => {
    fetchAuthorizations();
  }, [selectedProjectID]);

  useEffect(() => {
    setEditMode(false);
    if (Object.keys(selectedDocumentation).length > 0) {

      const bodyData = selectedDocumentation?.parameters?.body;
      if (checkType(bodyData) === "String") {
        setParameters(extractParamsAndTypes(bodyData));
      } else if (checkType(bodyData) === "Array") {
        setParameters(bodyData);
      } else if (typeof bodyData === "object" && bodyData !== null) {
        setParameters(extractParamsAndTypes(JSON.stringify(bodyData)));
      } else {
        setParameters([]);
      }

      setTitle(selectedDocumentation?.parameters?.title);
      setSelectedMethod(selectedDocumentation?.parameters?.method);
      setEditBaseURL(
        extractBaseUrlAndEndpoint(selectedDocumentation?.parameters?.url)
          .baseUrl
      );
      setEditEndpoint(
        extractBaseUrlAndEndpoint(selectedDocumentation?.parameters?.url)
          .endpoint
      );

      const htmlContent = selectedDocumentation?.description || "";
      const blocksFromHTML = convertFromHTML(htmlContent);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [selectedDocumentation]);

  return (
    <div className="documentation-container">
      <ModuleSidebar
        title={getTranslation("collections")}
        menus={menus}
        selectedSubmenu={selectedDocumentationID}
        onSubmenuClick={handleSubmenuClick}
        showSearch={true}
      />
      <div className="documentation-content">
        <div className="documentation-inner-content">
          {!isEmptyObject(selectedDocumentation) ? (
            <>
              {hasWritePermission && (
                <button
                  className="documentation-edit-button"
                  onClick={() => handleEditClicked(editMode)}
                >
                  {editMode ? getTranslation("save") : getTranslation("edit")}
                </button>
              )}

              {editMode && (
                <button
                  className="documentation-cancel-button"
                  style={{ marginLeft: "10px" }}
                  onClick={handleCancelEdit}
                >
                  {getTranslation("cancel")}
                </button>
              )}
              {!editMode && (
                <button
                  className="documentation-cancel-button"
                  style={{ marginLeft: "10px", height: "auto" }}
                  onClick={() => handleExportPDF("api-documentation-pdf")}
                >
                  {getTranslation("exportToPdf")}
                </button>
              )}
              <button
                className="documentation-cancel-button documentation-recreate-button"
                style={{
                  marginLeft: "10px",
                  height: "auto",
                  top: editMode ? 120 : 135,
                }}
                onClick={handleReCreate}
              >
                {getTranslation("reCreate")}
              </button>
              <div className="documentation-title-holder">
                {editMode ? (
                  <>
                    <div className="documentation-edit-container">
                      <span className="documentation-title">
                        {getTranslation("title").toUpperCase()}:
                      </span>
                      <input
                        className="documentation-input"
                        type="text"
                        maxLength={30}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>
                    <div className="documentation-edit-container">
                      <span className="documentation-title">METHOD:</span>
                      <Dropdown
                        style={{ width: "65%", marginLeft: "5%" }}
                        options={methodOptions}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        value={selectedMethod}
                      />
                    </div>
                    <div className="documentation-edit-container">
                      <span className="documentation-title">BASE URL:</span>
                      <input
                        className="documentation-input"
                        type="text"
                        value={editBaseURL}
                        onChange={(e) => setEditBaseURL(e.target.value)}
                      />
                    </div>
                    <div className="documentation-edit-container">
                      <span className="documentation-title">ENDPOINT:</span>
                      <input
                        className="documentation-input"
                        type="text"
                        value={editEndpoint}
                        onChange={(e) => setEditEndpoint(e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <span className="documentation-title">
                      {selectedDocumentation?.title || "-"}
                    </span>
                    <span className="documentation-title">
                      METHOD:{" "}
                      {selectedDocumentation?.parameters?.method?.toUpperCase() ||
                        "-"}
                    </span>
                    <span className="documentation-title">
                      BASE URL: {extractBaseUrlAndEndpoint(selectedDocumentation?.parameters?.url).baseUrl}
                    </span>

                    <span className="documentation-title">
                      ENDPOINT: {extractBaseUrlAndEndpoint(selectedDocumentation?.parameters?.url).endpoint}
                    </span>
                  </>
                )}
              </div>
              {editMode ? (
                <div style={{ marginTop: '25px' }}>
                  <RichTextEditor
                    editorState={editorState}
                    setEditorState={setEditorState}
                  />
                </div>

              ) : (
                <div className="documentation-description-container">
                  {getDescriptionContent()}
                </div>
              )}
              <div
                style={{ marginTop: "20px" }}
                className="documentation-title"
              >
                {getTranslation("parameters")}
              </div>
              {parameters.length === 0 && !editMode && (
                <div className="documentation-description-container">
                  <span>{getTranslation("noDescription")}</span>
                </div>
              )}
              {editMode && (
                <button
                  className="documentation-button"
                  style={{ marginTop: "25px", marginBottom: "25px" }}
                  onClick={handleAddParameter}
                >
                  <TbPlus style={{ marginRight: "10px", marginTop: "2px" }} />
                  {getTranslation("addParameter")}
                </button>
              )}
              {parameters?.map((param, index) => (
                <div key={index} className="documentation-param-item-row">
                  {editMode ? (
                    <>
                      <input
                        type="text"
                        placeholder={getTranslation("name")}
                        value={param.key}
                        onChange={(e) =>
                          handleInputChange(index, "key", e.target.value)
                        }
                        className="documentation-input"
                      />
                      <Dropdown
                        options={typeOptions}
                        onChange={(e) =>
                          handleInputChange(index, "type", e.target.value)
                        }
                        value={param.type}
                      />
                      <input
                        type="text"
                        placeholder={getTranslation("description")}
                        value={param.description}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className="documentation-input"
                      />
                      <label className="documentation-checkbox-label">
                        <input
                          type="checkbox"
                          checked={param.required}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "required",
                              e.target.checked
                            )
                          }
                        />
                        {getTranslation("required")}
                      </label>
                      <button
                        onClick={() => handleDeleteParameter(index)}
                        className="documentation-button"
                      >
                        {getTranslation("delete")}
                      </button>
                    </>
                  ) : (
                    <>
                      <label className="param-item-key-label">
                        {param.key}
                      </label>
                      <label className="param-item-type-label">
                        {param.type}
                      </label>
                      <label className="param-item-description-label">
                        {param.description || "-"}
                      </label>
                      <label className="param-item-required-label">
                        {param.required ? getTranslation("required") : "-"}
                      </label>
                    </>
                  )}
                </div>
              ))}
            </>
          ) : (
            <span className="documentation-empty-text">
              {getTranslation("selectApi")}
            </span>
          )}
        </div>
      </div>

      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
        <ApiDocumentation data={selectedDocumentation} />
      </div>
    </div>
  );
}
